import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Formik, Form, FormikValues } from 'formik';
import * as yup from 'yup';

import PhoneInput from 'components/PhoneInput';
import ResponsiveInput from 'components/ResponsiveInput';
import { CountryCode } from 'components/Selects/CountryCodeSelect';
import useStoreTranslation from 'hooks/useStoreTranslation';

import { Container, Header, FieldsContainer, BackButton, NextButton, ButtonsContainer, ButtonLine } from '../../styles';
import { CountryValues } from '../Country';
import { PhoneHint } from './styles';

export interface AdministratorValues {
  name: string;
  lastName: string;
  phoneNumber: string;
  countryCode: CountryCode;
}

interface Props {
  values: AdministratorValues | null;
  onSubmit: (values: AdministratorValues) => Promise<any>;
  countryCodes: CountryCode[];
  country: CountryValues | null;
}

const Administrator = ({ values, onSubmit, countryCodes, country: countryValues }: Props) => {
  const { t } = useStoreTranslation();
  const history = useHistory();

  const [loading, setLoading] = useState(false);

  if (!countryValues) return null;

  const validationSchema = yup.object().shape({
    name: yup.string().trim().required().max(100),
    lastName: yup.string().trim().required().max(100),
    phoneNumber: yup.string().phone().required(),
    countryCode: yup.object().required(),
  });

  const initialValues: AdministratorValues = {
    name: '',
    lastName: '',
    phoneNumber: '',
    countryCode: countryCodes.find((c) => c.id === countryValues.country.id) as CountryCode,
  };

  const handleBack = () => history.goBack();

  const handleSubmit = async (formValues: AdministratorValues) => {
    setLoading(true);
    await onSubmit(formValues);
    setLoading(false);
  };

  return (
    <Formik
      initialStatus={{ rightCheckIfIsValid: true }}
      initialValues={values || initialValues}
      isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isValid }: FormikValues) => (
        <Form>
          <Container>
            <Header>{t('registrationOnboarding.administrator.title')}</Header>

            <FieldsContainer>
              <ResponsiveInput name="name" placeholder={t('registrationOnboarding.administrator.namePlaceholder')} />

              <ResponsiveInput
                name="lastName"
                placeholder={t('registrationOnboarding.administrator.surnamePlaceholder')}
              />

              <PhoneInput
                mask={'9'.repeat(50)}
                phoneField="phoneNumber"
                countryCodeField="countryCode"
                countryCodes={countryCodes}
              />

              <PhoneHint>{t('registrationOnboarding.administrator.telephoneHint')}</PhoneHint>
            </FieldsContainer>

            <ButtonsContainer>
              <ButtonLine>
                <BackButton type="button" onClick={handleBack}>
                  {t('registrationOnboarding.backButton')}
                </BackButton>
                <NextButton disabled={!isValid || loading} loading={loading}>
                  {t('registrationOnboarding.nextButton')}
                </NextButton>
              </ButtonLine>
            </ButtonsContainer>
          </Container>
        </Form>
      )}
    </Formik>
  );
};

export default Administrator;
